import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { API } from "../lib/scroll";
import { LOGO_MARK } from "../data/content";
import { EASE } from "./Reveal";

const STARTERS = ["What services do you offer?", "Do you serve my area?", "I'd like to start a project."];

const isValidPhone = (p) => {
  const digits = p.replace(/\D/g, "");
  return /^\+?[0-9\s\-().]+$/.test(p.trim()) && digits.length >= 7 && digits.length <= 15;
};

export const AIChat = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [leadName, setLeadName] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [leadError, setLeadError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    const saved = sessionStorage.getItem("nagel-session");
    if (saved) {
      try {
        const { sessionId: sid, name } = JSON.parse(saved);
        setSessionId(sid);
        setLeadName(name);
        setMessages([{ role: "assistant", text: `Welcome back, ${name.split(" ")[0]}. What can I help you with today?` }]);
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadError("");
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      setLeadError("Please fill in all three fields.");
      return;
    }
    if (!isValidPhone(form.phone)) {
      setPhoneError("Please enter a valid phone number — digits only, 7 to 15 digits, with an optional + for international numbers.");
      return;
    }
    setPhoneError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setLeadName(form.name);
      sessionStorage.setItem("nagel-session", JSON.stringify({ sessionId: data.session_id, name: form.name }));
      setMessages([{ role: "assistant", text: `Hi ${form.name.split(" ")[0]} — welcome to Nagel Landscaping. Ask me anything about our services, service area, or starting a project.` }]);
    } catch {
      setLeadError("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy || !sessionId) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const payload = p.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const { token } = JSON.parse(payload);
            if (token) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + token };
                return c;
              });
            }
          } catch { /* ignore partial */ }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: "Sorry — connection issue. Please call (209) 545-1696 and the team will help right away." };
        return c;
      });
    }
    setBusy(false);
  };

  const fieldCls = "w-full rounded-xl border border-charcoal/15 bg-white/70 px-4 py-3 text-sm outline-none focus:border-flame transition-colors";

  return (
    <>
      <motion.button
        data-testid="ask-nagel-floating-button"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 260, damping: 20 }}
        onClick={() => setOpen(true)}
        className="group fixed bottom-20 left-4 sm:bottom-6 sm:left-6 z-50 flex items-center"
        aria-label="Ask Nagel AI assistant"
      >
        <span className="w-12 h-12 shrink-0 rounded-full overflow-hidden shadow-[0_10px_30px_rgba(240,90,36,0.45)] transition-transform duration-300 group-hover:scale-105">
          <img src={LOGO_MARK} alt="" className="w-full h-full object-cover" />
        </span>
        <span className="max-w-0 group-hover:max-w-[150px] overflow-hidden transition-all duration-500 whitespace-nowrap">
          <span className="ml-2 inline-block rounded-full texture-grain text-white text-xs font-bold tracking-[0.1em] px-5 py-3.5 shadow-[0_10px_30px_rgba(240,90,36,0.4)]">
            ASK NAGEL
          </span>
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-panel"
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="fixed bottom-36 sm:bottom-24 left-4 sm:left-6 z-50 w-[min(400px,calc(100vw-2rem))] h-[540px] max-h-[72vh] rounded-3xl bg-white/80 backdrop-blur-2xl border border-charcoal/10 shadow-[0_24px_80px_rgba(18,19,20,0.25)] flex flex-col overflow-hidden"
            data-lenis-prevent
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-charcoal/[0.07]">
              <div className="flex items-center gap-3">
                <img src={LOGO_MARK} alt="Nagel sun mark" className="w-9 h-9 rounded-full object-cover shrink-0" />
                <div>
                  <p className="font-display font-semibold text-sm text-charcoal tracking-tight">ASK NAGEL</p>
                  <p className="font-mono text-[10px] tracking-[0.18em] text-charcoal/45">LANDSCAPE ASSISTANT</p>
                </div>
              </div>
              <button
                data-testid="ai-panel-close-button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-charcoal/5 flex items-center justify-center text-charcoal/60 transition-colors"
                aria-label="Close assistant"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {!sessionId ? (
              <form onSubmit={submitLead} className="flex-1 flex flex-col justify-center px-6 gap-3.5 overflow-y-auto" data-lenis-prevent>
                <p className="font-display font-medium text-xl text-charcoal tracking-tight">Before we start —</p>
                <p className="text-sm text-charcoal/60 -mt-2 mb-2">Leave your details so our team can follow up.</p>
                <input
                  data-testid="ai-lead-form-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Name"
                  className={fieldCls}
                />
                <div>
                  <input
                    data-testid="ai-lead-form-phone-input"
                    value={form.phone}
                    onChange={(e) => { setForm({ ...form, phone: e.target.value }); setPhoneError(""); }}
                    onBlur={() => { if (form.phone.trim() && !isValidPhone(form.phone)) setPhoneError("Please enter a valid phone number — digits only, 7 to 15 digits, with an optional + for international numbers."); }}
                    placeholder="Phone"
                    type="tel"
                    className={`${fieldCls} ${phoneError ? "border-ember" : ""}`}
                  />
                  {phoneError && <p data-testid="ai-lead-form-phone-error" className="mt-1.5 text-xs text-ember leading-snug">{phoneError}</p>}
                </div>
                <input
                  data-testid="ai-lead-form-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email"
                  type="email"
                  className={fieldCls}
                />
                {leadError && <p data-testid="ai-lead-form-error" className="text-xs text-ember">{leadError}</p>}
                <button
                  data-testid="ai-lead-form-submit-button"
                  type="submit"
                  disabled={busy}
                  className="mt-1 rounded-xl bg-charcoal text-cream py-3.5 text-sm font-bold tracking-wide hover:bg-flame transition-colors duration-300 disabled:opacity-50"
                >
                  {busy ? "ONE MOMENT…" : "START CHATTING"}
                </button>
              </form>
            ) : (
              <>
                <div ref={scrollRef} data-testid="ai-chat-messages" data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-3" style={{ touchAction: "pan-y" }}>
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        m.role === "user" ? "bg-charcoal text-cream rounded-br-md" : "bg-charcoal/[0.05] text-charcoal rounded-bl-md"
                      }`}>
                        {m.text}
                        {m.role === "assistant" && busy && i === messages.length - 1 && !m.text && (
                          <span className="inline-block w-1.5 h-3.5 bg-ember animate-pulse" />
                        )}
                      </div>
                    </div>
                  ))}
                  {messages.length <= 1 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {STARTERS.map((s) => (
                        <button
                          key={s}
                          data-testid={`ai-starter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => send(s)}
                          className="rounded-full border border-charcoal/15 px-3.5 py-2 text-xs text-charcoal/70 hover:border-flame hover:text-ember transition-colors duration-300"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-charcoal/[0.07]">
                  <div className="flex items-center gap-2 rounded-full border border-charcoal/15 bg-white/70 pl-4 pr-1.5 py-1.5">
                    <input
                      data-testid="ai-chat-input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && send()}
                      placeholder="Ask about services, areas, projects…"
                      className="flex-1 bg-transparent text-sm outline-none"
                    />
                    <button
                      data-testid="ai-chat-send-button"
                      onClick={() => send()}
                      disabled={busy || !input.trim()}
                      className="w-9 h-9 rounded-full texture-grain flex items-center justify-center text-white disabled:opacity-40 transition-opacity"
                      aria-label="Send message"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
